import { Stack } from '@mui/material';

import Note from '@/designs-dev/annotations/Note';
import WelcomeScreen from '@/karehero-app/auth/WelcomeScreen';
import KindeHostedSheet from '@/karehero-app/auth/KindeHostedSheet';
import AccountLoadingScreen from '@/karehero-app/access/AccountLoadingScreen';
import FundingScreen from '@/karehero-app/access/FundingScreen';
import WorkEmailScreen from '@/karehero-app/access/WorkEmailScreen';
import PartnerCodeScreen from '@/karehero-app/access/PartnerCodeScreen';
import PayrollIdScreen from '@/karehero-app/access/PayrollIdScreen';
import AccessVerifiedScreen from '@/karehero-app/onboarding/current-flow/AccessVerifiedScreen';
import { ACCESS_ERRORS } from '@/karehero-app/access/access.content';
import { type HandoverSpec } from '@/designs-dev/handover/types';

const noop = () => {};

// The app bar the steps after registration carry: the logo, and the avatar that
// opens Settings. No initials in it, because the name is asked for later.
const account = { onSignOut: noop };

const welcome = (
  <WelcomeScreen
    onCreateAccount={noop}
    onSignIn={noop}
    onForgotPassword={noop}
  />
);

// Where a half-finished sign-up resumes. Exported because the master flows page
// says the same thing under its own heading, and two copies of a rule about
// which screen somebody lands on is one copy too many. The opening sentence
// comes out on its own for the page that wants the rule without the three
// screens it resolves to.
export const verificationIntro = (
  <span>
    A member who is signed in but has not finished this does not start again.
    Where they land is whichever of these is true.
  </span>
);

export const verificationRules = (
  <Stack spacing={1.5}>
    {verificationIntro}
    <span>
      We do not know how they are paying: the funding question, screen six.
    </span>
    <span>
      We know how they are paying but have no identifier for it: the screen that
      answer asks for, so work email, partner code or payroll ID, screen seven.
    </span>
    <span>Both answered and checked: straight to You&rsquo;re in.</span>
  </Stack>
);

// Sign-up on its own, as it is being designed. The flows go in as they are
// drawn, the way the intake page was built up.
//
// Separate from Concepting next door, which is the whole front door as it stands
// today — welcome, Kinde, access checks and the questions. This page is only the
// part being reworked, so it does not move when that one is reshuffled. The
// steps below are written out here for the same reason: Concepting is free to be
// reshuffled and this page should not move with it.
export const signUpSpec: HandoverSpec = {
  id: 'sign-up',
  title: 'Sign up',
  description: '',
  links: [],
  flows: [
    {
      // Screens 1 to 8: the welcome screen you arrive on, Kinde's three hosted
      // pages, the interstitial while the account is made, and the access check.
      // The questions carry on from screen 8 and are drawn on Concepting.
      title: 'Sign up to home',
      steps: [
        {
          id: 'welcome',
          title: 'Welcome',
          tap: 'Open the app',
          render: () => welcome,
        },
        {
          id: 'kinde-register',
          title: 'Create account (Kinde)',
          tap: 'Tap ‘Create account’',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet variant='register' onClose={noop} />
            </>
          ),
        },
        {
          id: 'kinde-code',
          title: 'Check your inbox (Kinde)',
          tap: 'Tap ‘Create your account’',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet
                variant='register'
                initialStep='code'
                onClose={noop}
              />
            </>
          ),
        },
        {
          id: 'kinde-password',
          title: 'Set your password (Kinde)',
          tap: 'Enter the emailed code',
          render: () => (
            <>
              {welcome}
              <KindeHostedSheet
                variant='register'
                initialStep='password'
                onClose={noop}
              />
            </>
          ),
        },
        {
          id: 'loading',
          title: 'Setting up your account',
          tap: 'Tap ‘Continue’',
          render: () => <AccountLoadingScreen onContinue={noop} />,
        },
        {
          id: 'funding',
          title: 'Welcome & funding',
          tap: 'Loader completes',
          render: () => (
            <FundingScreen
              onContinue={noop}
              onSignOut={noop}
              initialChoice='work'
            />
          ),
          alt: [
            {
              title: 'I have a partner code',
              render: () => (
                <FundingScreen
                  onContinue={noop}
                  onSignOut={noop}
                  initialChoice='partner'
                />
              ),
            },
            {
              title: 'I have a payroll ID',
              render: () => (
                <FundingScreen
                  onContinue={noop}
                  onSignOut={noop}
                  initialChoice='payroll'
                />
              ),
            },
          ],
        },
        {
          id: 'verify',
          title: 'Verify access',
          tap: '‘Through work’',
          render: () => (
            <WorkEmailScreen onSubmit={noop} onBack={noop} account={account} />
          ),
          alt: [
            {
              title: 'Partner code',
              render: () => (
                <PartnerCodeScreen
                  onSubmit={noop}
                  onBack={noop}
                  account={account}
                />
              ),
            },
            {
              title: 'Payroll ID',
              render: () => (
                <PayrollIdScreen
                  onSubmit={noop}
                  onBack={noop}
                  account={account}
                />
              ),
            },
          ],
        },
        {
          id: 'verified',
          title: 'You’re in',
          tap: 'Code checks out',
          render: () => <AccessVerifiedScreen onContinue={noop} />,
        },
      ],
    },
    {
      // Called Verification because that is the thing being resolved: main's own
      // name for this half of sign-up (VerifyAccess, and the verification
      // configuration behind it). The flow above is the first run through it;
      // this is what happens on every run after that.
      //
      // Steps deliberately absent. The screens are the six, seven and eight
      // above, and drawing them twice would say the rules are a second flow
      // rather than which of those three a return lands on.
      title: 'Verification',
      note: (
        <Note title='Readme' variant='body2'>
          {verificationRules}
        </Note>
      ),
      steps: [],
    },
  ],
  // The bar's one action, then what the checks say when they say no.
  //
  // What the checks say when they say no, in production's words rather than
  // ours: the messages come from ACCESS_ERRORS, which is copied out of main.
  // The screens are being reworked; the failures are not, so the copy is the
  // copy. Where main raises one as a toast, these show it under the field, which
  // is the one thing that moved.
  states: [
    {
      // The avatar is the only thing in the app bar that does anything, so what
      // it does is a screen rather than a line in a readme.
      id: 'settings',
      title: 'Settings, from the avatar',
      kind: 'view',
      repro:
        'Tap the avatar on any step that has the bar. Help opens the Intercom messenger, which is not ours to draw',
      render: () => (
        <FundingScreen
          onContinue={noop}
          onSignOut={noop}
          initialChoice='work'
          initialSheetOpen
        />
      ),
    },
    {
      id: 'work-email-unknown',
      title: 'The work email is not one we know',
      kind: 'error',
      row: 'Work email (7)',
      repro: 'Register by domain rejects the address',
      render: () => (
        <WorkEmailScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.workEmailUnknown}
        />
      ),
    },
    {
      id: 'work-email-unexpected',
      title: 'The request comes back unusable',
      kind: 'error',
      row: 'Work email (7)',
      repro: 'A response we cannot read. Retrying is the only way on',
      render: () => (
        <WorkEmailScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.workEmailUnexpected}
        />
      ),
    },
    {
      id: 'partner-code-invalid',
      title: 'The partner code is wrong or spent',
      kind: 'error',
      row: 'Partner code (7B)',
      repro: 'Typed by hand and the check says no',
      render: () => (
        <PartnerCodeScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.partnerCodeInvalid}
        />
      ),
    },
    {
      id: 'partner-code-from-link',
      title: 'The code in their link no longer works',
      kind: 'error',
      row: 'Partner code (7B)',
      repro:
        'Arrived on a partner link. The code was checked before they typed anything, so the message names the link',
      render: () => (
        <PartnerCodeScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.partnerCodeFromLink}
        />
      ),
    },
    {
      id: 'partner-code-server',
      title: 'The check itself is down',
      kind: 'error',
      row: 'Partner code (7B)',
      repro: '5xx from the code check, so the code may well be fine',
      render: () => (
        <PartnerCodeScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.partnerCodeServer}
        />
      ),
    },
    {
      id: 'payroll-id-length',
      title: 'The payroll ID is the wrong length',
      kind: 'error',
      row: 'Payroll ID (7C)',
      repro:
        'Production checks the length before the code. The screen keeps Continue disabled until six, so this is the state it is preventing',
      render: () => (
        <PayrollIdScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.payrollIdLength}
        />
      ),
    },
    {
      id: 'payroll-id-wrong',
      title: 'Six characters, but not the ones we sent',
      kind: 'error',
      row: 'Payroll ID (7C)',
      repro:
        'Submitted as an invite key and rejected. The message names the email it is in, subject and all',
      render: () => (
        <PayrollIdScreen
          onSubmit={noop}
          onBack={noop}
          account={account}
          error={ACCESS_ERRORS.payrollIdWrong}
        />
      ),
    },
  ],
  components: {},
};
